import { Link } from "react-router-dom";
import { Ear, HeartHandshake, Users, Megaphone, ArrowRight } from "lucide-react";
import { KineticLines } from "../components/KineticLines";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { SectionHead } from "../components/SectionHead";
import { IMAGES } from "../data/content";

const VALUES = [
  { icon: Ear, title: "Ascolto", text: "Prima di proporre, ascoltiamo. Ogni iniziativa nasce da un bisogno reale raccolto tra i cittadini." },
  { icon: HeartHandshake, title: "Cura", text: "Spazi pubblici, ambiente, periferie: ci prendiamo cura di ciò che è di tutti, come fosse di ciascuno." },
  { icon: Users, title: "Solidarietà", text: "Costruiamo reti di aiuto concreto, perché nessuno nella nostra città debba sentirsi solo." },
  { icon: Megaphone, title: "Partecipazione", text: "Porte aperte e bilanci trasparenti: chi vuole contribuire trova sempre spazio e voce." },
];

export default function ChiSiamo() {
  return (
    <>
      <section data-testid="chi-siamo-hero" className="mx-auto max-w-[1400px] px-5 pb-16 pt-28 md:px-10 md:pt-40 md:pb-24">
        <Reveal y={12}>
          <div className="mb-8 flex items-center gap-3">
            <span className="h-2.5 w-2.5 bg-orange" aria-hidden="true" />
            <span className="font-oswald text-xs uppercase tracking-[0.3em] text-navy md:text-sm">Chi siamo</span>
          </div>
        </Reveal>
        <KineticLines
          lines={["Un movimento", <span key="c" className="text-orange">di cittadini.</span>]}
          className="font-oswald text-[clamp(2.8rem,8vw,7rem)] font-semibold uppercase leading-[0.95] tracking-tight"
        />
        <Reveal delay={0.5} className="mt-10 grid gap-10 md:grid-cols-2">
          <p data-testid="chi-siamo-lead" className="font-playfair text-xl italic leading-relaxed text-navy md:text-2xl">
            «La città non appartiene a chi la amministra, ma a chi la abita ogni giorno.»
          </p>
          <div className="space-y-5 text-sm leading-relaxed opacity-80 md:text-base">
            <p>
              Progetto Città nasce tra Trapani ed Erice da un gruppo di vicini di casa, commercianti, genitori,
              studenti e pensionati accomunati da un'idea semplice: per cambiare le cose bisogna esserci, di persona
              e ogni giorno.
            </p>
            <p>
              Non siamo un partito e non rispondiamo a nessuno se non ai cittadini. Ci organizziamo in assemblee
              aperte, decidiamo insieme le priorità e ci rimbocchiamo le maniche: dai banchetti informativi alle
              giornate di cura del territorio, fino alle proposte portate nelle sedi istituzionali. Oggi il nostro impegno
              è concentrato su Trapani ed Erice, con lo sguardo aperto, domani, agli altri comuni della provincia.
            </p>
          </div>
        </Reveal>
      </section>

      <Marquee items={["Nati dal basso", "Indipendenti", "Aperti a tutti"]} testId="chi-siamo-marquee" />

      <section data-testid="values-section" className="mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-32">
        <SectionHead kicker="I nostri valori" title="In cosa crediamo." />
        <div className="grid gap-px border border-ink bg-ink sm:grid-cols-2">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08} className="bg-bone">
              <div data-testid={`value-card-${i}`} className="group h-full p-8 transition-colors duration-300 hover:bg-navy/5 md:p-12">
                <v.icon className="h-8 w-8 text-orange transition-transform duration-300 group-hover:-translate-y-1" aria-hidden="true" />
                <h3 className="mt-6 font-oswald text-2xl font-semibold uppercase tracking-tight md:text-3xl">{v.title}</h3>
                <p className="mt-4 max-w-sm text-sm leading-relaxed opacity-80">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="community-section" className="mx-auto max-w-[1400px] px-5 pb-20 md:px-10 md:pb-32">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-7">
            <div className="overflow-hidden border border-ink">
              <img
                src={IMAGES.community}
                alt="La costa della provincia di Trapani vista dall'alto"
                loading="lazy"
                className="aspect-[16/10] w-full object-cover transition-transform duration-700 ease-out hover:scale-[1.03]"
              />
            </div>
          </Reveal>
          <div className="lg:col-span-5">
            <SectionHead kicker="Il metodo" title="Poche regole, chiare." className="mb-8" />
            <ul className="space-y-6 border-t border-ink pt-8 text-sm leading-relaxed md:text-base">
              {[
                "Ogni decisione viene discussa in assemblea aperta.",
                "Chi porta un'idea la segue fino in fondo, con l'aiuto di tutti.",
                "Trasparenza totale su fondi, iniziative e risultati.",
              ].map((r, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <li data-testid={`method-rule-${i}`} className="flex gap-4">
                    <span className="font-playfair text-2xl font-bold italic text-orange">{String(i + 1).padStart(2, "0")}</span>
                    <span className="opacity-80">{r}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
            <Reveal delay={0.2} className="mt-10">
              <Link
                to="/membri"
                data-testid="chi-siamo-members-link"
                className="group inline-flex items-center gap-2 font-oswald text-sm uppercase tracking-widest text-navy transition-colors duration-300 hover:text-orange"
              >
                Conosci le persone <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
